/* eslint-disable max-lines */
// internal deps
import { Option } from "@swiggy-private/js-fp";
import {
    PaymentMethod,
    PaymentMethodMeta,
    UpiPaymentMethod,
    CardPaymentMethodData,
    NetBankingPaymentMethodData,
    WalletPaymentMethodData,
    CredPaymentMethod,
} from "@swiggy-private/payment-ui";
import {
    isEmpty,
    VALID_PAYMENT_GROUP_TYPES,
    VALID_PAYMENT_METHODS,
    VALID_UPI_PAYMENT_CODE,
    VALID_WALLET_TYPES,
} from "@swiggy-private/payment-core";
import {
    CheckoutMetaInfoResponsePayload,
    JuspayCardResponsePayload,
    AmazonSdkProceedToPayResponseData,
} from "@swiggy-private/native-bridge";
import { captureError, isAndroidPlatform, safeJsonParse } from "@swiggy-private/core-ui";
import {
    OrderData,
    OrderApiData,
    RawOrderApiData,
    PendingPaymentParams,
    PAYMENT_CODES,
    OnPayExtraParams,
    JuspayTokenMetaInterface,
    AppRedirectionInfo,
    MARKETPLACE_CATEGORY,
} from "@swiggy-private/payment-types";

import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

import {
    getBridgeSdk,
    GlobalHeaderState,
    recordNREvent,
    PlaceOrderData,
    SwgyHeaderKeysList,
    CheckoutActionTypes,
    NEWRELIC_TABLES,
    setPersistOrderDataInLocalStorage,
    SetDataSource,
    isBusinessLineRegular,
    isBusinessCategoryInstamart,
    handleMwebUpiIntentRedirect,
    shouldUseGpoUpiIntentAppsOnWebCheckout,
} from "@swiggy-private/checkout-core";

// helpers
import { showApiError } from "./ApiHelper";
import { isValidURI } from "../utils/URI";
import { getOrderConfirmationRedirectUrl, getPaymentCardToken, isAddNewCardPayment } from "./PaymentHelper";
import { generatePhonepeRedirectUrl } from "./WalletHelper";
import {
    getCardTokenizeValue,
    payUsingJusPayRedirectFlow,
    shouldUseJuspayRedirectFlow,
    resolveClientAuthTokenDataForMerchantId,
    resolveMerchantId,
} from "./JuspayHelpers";
import { JuspayClientAuthTokenData } from "@payment-client/interfaces/Juspay";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import {
    clearWebviewRefreshSavedState,
    getVersionCode,
    isBusinessLineDineout,
    isBusinessLineCBCC,
    isBusinessLineSwiggyMoney,
    isBusinessLineSwiggyOne,
    isIos,
    isOriginalBusinessLineSwiggyMoney,
    saveUsersLastStateDuringProcessOrder,
} from "./Misc";
import { getSwiggyMoneyReceivedRedirectUrl } from "./SwiggyInstrumentsHelper";
import { isPaymentMethodUpiAndPaymentCodePhonePeUpi, isPaymentMethodUpiAndPaymentCodeUpiIntent } from "./UpiHelper";
import { getTurboPaymentMethodFromPlaceOrder } from "./TurboPayment";
import { getCardBinFromExtraParams } from "./CardHelper";
import { SESSION_STORAGE_KEYS, Storage } from "@swiggy-private/core-ui";
import { handlePayUsingInAppUpi, isInAppUpiPaymentMethod } from "./InAppUpiHelper";
import { isCreditCardEMIPaymentMethod } from "./CreditCardEMIHelper";
import {
    AMAZON_PAY_CUSTOM_EVENTS,
    AMAZON_PAY_NB_STATES,
    APAY_NB_CALL_STATUS,
} from "@payment-client/constants/AmazonPay";
import { recordNativeBridgeCall, recordNewSdkFlowEvent } from "@payment-client/helpers/AmazonPayNREvents";

// api
import { PlaceOrderParams, ConfirmOrderParams } from "@payment-client/apis/Order";
import { GetAuthTokenParams, getJuspayClientAuthToken } from "@payment-client/apis/Juspay";

// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";
import {
    PLACE_ORDER_EVENT_TYPE,
    ALLOWED_METHODS_FOR_PENDING_PAYMENT,
    ALLOWED_METHODS_APP_STATE_IN_IDLE_MODE,
    CASH,
} from "@payment-client/constants/Order";
import { SUPER_ORDER_TAG, PRIORITY_DELIVERY_TAG } from "@payment-client/constants/Order";
import {
    DEFAULT_JUSPAY_MERCHANTID,
    JUSPAY_END_URLS_REGEX,
    JUSPAY_SWIGGY_RETURN_URL,
    SM_JUSPAY_END_URLS_REGEX,
} from "@payment-client/constants/Juspay";
import { notAllowedWebviewRefreshVersionCodes } from "@payment-client/constants/WebviewRefresh";
import { GIFT_DETAILS_STORAGE_KEY, PLACE_ORDER_DATA } from "@payment-client/constants/StorageKeys";

// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { POST_ORDER_STATUS, ProcessOrderActionPayload, UPI_NB_STATUS } from "@payment-client/interfaces/Checkout";
import { PlaceOrderSuccessHandlerReturnType } from "@payment-client/interfaces/Order";
import { CredOfferLayerDetailsInterface } from "@swiggy-private/native-bridge";

// contexts
import { AmazonPaySdkContextValue } from "@payment-client/contexts/AmazonPaySdkContext";
import { GiftDetails } from "@payment-client/interfaces/SwiggyMoney";

import { handleAppRedirectionInfoConversion } from "@payment-client/helpers/AppRedirectionHelper";
import { getPublicJuspayClientAuthToken } from "@payment-client/apis/publicJuspay";

export const isUpiCollectPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is UpiPaymentMethod => {
    return (
        method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI &&
        method.paymentCode.toUpperCase() === VALID_UPI_PAYMENT_CODE.UPI_COLLECT
    );
};

export const isCardPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is CardPaymentMethodData => {
    return [VALID_PAYMENT_GROUP_TYPES.CARD, VALID_PAYMENT_GROUP_TYPES.FOOD_CARD].includes(
        method.groupName.toUpperCase(),
    );
};

export const isCredPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is CredPaymentMethod => {
    return [VALID_PAYMENT_GROUP_TYPES.CRED].includes(method.groupName.toUpperCase());
};

export const isNetBankingPaymentMethod = (
    method: PaymentMethod<PaymentMethodMeta>,
): method is NetBankingPaymentMethodData => {
    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.NETBANKING;
};

export const isWalletPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is WalletPaymentMethodData => {
    return [VALID_PAYMENT_GROUP_TYPES.WALLET, VALID_PAYMENT_GROUP_TYPES.WALLET_WEB].includes(
        method.groupName.toUpperCase(),
    );
};

export const isPaymentMethodAmazonPay = (method: PaymentMethod<PaymentMethodMeta>): boolean => {
    return (
        method.paymentCode.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAYWEB ||
        method.paymentCode.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAY
    );
};

export const isPaymentMethodAmazonPaySDK = (method: PaymentMethod<PaymentMethodMeta>): boolean =>
    method.paymentCode.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAY;

export const isPaymentMethodAmazonPayLater = (method: PaymentMethod<PaymentMethodMeta>): boolean =>
    method.paymentCode.toUpperCase() === PAYMENT_CODES.AMAZONPAYLATER.toUpperCase();

export const isPaymentMethodPhonePe = (method: PaymentMethod<PaymentMethodMeta>): boolean => {
    return method.name.toUpperCase() === VALID_WALLET_TYPES.PHONEPE;
};

export function getUpiCollectPaymentParams(
    paymentData: UpiPaymentMethod,
    extraParams?: {
        [key: string]: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payer_vpa: string;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            save_instrument: boolean;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            transaction_mode: string;
        };
    },
): { [key: string]: unknown } {
    // to handle the case when new upi method is selected
    // & payment method doesn't contain data
    if ((!paymentData.meta || !paymentData.meta.payerVpa) && isEmpty(extraParams?.upi_transaction_info?.payer_vpa)) {
        return {};
    }

    return {
        /* eslint-disable @typescript-eslint/naming-convention */
        upi_transaction_info: {
            payer_vpa: paymentData.meta.payerVpa ?? extraParams?.upi_transaction_info?.payer_vpa ?? "",
            save_instrument: extraParams?.upi_transaction_info?.save_instrument ?? false,
            transaction_mode:
                paymentData.meta.transactionMode ?? extraParams?.upi_transaction_info?.transaction_mode ?? "",
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    };
}

export function getUpiIntentPaymentParams(paymentData: UpiPaymentMethod): { [key: string]: unknown } {
    return {
        /* eslint-disable @typescript-eslint/naming-convention */
        upi_transaction_info: {
            intent_app: paymentData.name,
            transaction_mode: paymentData.meta.transactionMode ?? "",
            intent_app_display_name: paymentData.displayName ?? "",
        },
        /* eslint-enable @typescript-eslint/naming-convention */
    };
}

export function getCardPaymentParams(
    paymentData: CardPaymentMethodData,
    extraParams: { [key: string]: unknown },
): { [key: string]: unknown } {
    const cardBin = getCardBinNumber(paymentData, extraParams);

    return {
        /* eslint-disable @typescript-eslint/naming-convention */
        card_bin_number: cardBin,
        tokenization_user_consent: !!extraParams.saveCard,
        token: paymentData.meta?.token ?? null,
        provider_category: paymentData.meta?.providerCategory ?? "",
        provider: paymentData.meta?.provider ?? "",
        cvv_mandatory: paymentData?.meta?.cvvMandatory ?? true,
        /* eslint-enable @typescript-eslint/naming-convention */
    };
}

export function getNetBankingPaymentParams(paymentData: NetBankingPaymentMethodData): { [key: string]: unknown } {
    return {
        /* eslint-disable @typescript-eslint/naming-convention */
        payment_netbanking_type: paymentData.nbCode,
        bankCode: paymentData.nbCode,
        /* eslint-enable @typescript-eslint/naming-convention */
    };
}

export const getPaymentCode = (paymentData: PlaceOrderData["paymentMethodData"], sdkAvailable: boolean): string => {
    if (isPaymentMethodAmazonPay(paymentData)) {
        return sdkAvailable ? paymentData.paymentCode : "AmazonPayWeb";
    }
    return paymentData.paymentCode;
};

const getPlaceOrderLocationParams = (): { lat: string; lng: string; addressId: string } => {
    return {
        lat: GlobalHeaderState.getHeader("lat") || "",
        lng: GlobalHeaderState.getHeader("lng") || "",
        addressId: GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartAddressId) || "",
    };
};

export const getOrderComments = (checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null) => {
    const rxInstructions = Storage.getItem(SESSION_STORAGE_KEYS.RX_SPECIAL_INSTRUCTIONS, "");

    if (!isEmpty(rxInstructions)) {
        return rxInstructions;
    }
    return checkoutMetaInfo?.orderComments || "";
};

export const getPlaceOrderParams = ({
    placeOrderData,
    apayState,
    isJuspaySdkAvailable,
    paymentReturnUrl,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    checkoutMetaInfo,
    paymentAmount,
}: {
    placeOrderData: PlaceOrderData;
    apayState: AmazonPaySdkContextValue["state"];
    isJuspaySdkAvailable: boolean;
    paymentReturnUrl?: Option<string>;
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
    paymentAmount?: string;
}): PlaceOrderParams => {
    const { paymentMethodData: paymentData } = placeOrderData;
    const { extraParams = {} } = placeOrderData;
    let placeOrderParamsData: PlaceOrderParams;

    // omitting out cvv and card number for not to be sending in place order calls
    const { cvv, cardNumber, paymentAmountWithoutDiscount, updatedPaymentAmount, ...otherExtraParams } = extraParams;
    const amazonPaySdkAvailable = apayState?.available ?? false;

    let paymentMethodParams = {};

    if (isUpiCollectPaymentMethod(paymentData)) {
        paymentMethodParams = getUpiCollectPaymentParams(paymentData);
    } else if (
        isPaymentMethodUpiAndPaymentCodeUpiIntent(paymentData) ||
        isPaymentMethodUpiAndPaymentCodePhonePeUpi(paymentData)
    ) {
        paymentMethodParams = getUpiIntentPaymentParams(paymentData);
    } else if (isCardPaymentMethod(paymentData)) {
        paymentMethodParams = getCardPaymentParams(paymentData, extraParams);
    } else if (isNetBankingPaymentMethod(paymentData)) {
        paymentMethodParams = getNetBankingPaymentParams(paymentData);
    }

    placeOrderParamsData = {
        ...paymentMethodParams,
        ...otherExtraParams,
        ...getPlaceOrderLocationParams(),
        paymentMethod: getPaymentCode(paymentData, amazonPaySdkAvailable),
        useJuspayNative: shouldUseJuspayRedirectFlow() ? false : isJuspaySdkAvailable,
        paymentReturnUrl: paymentReturnUrl?.unwrapOr(""),
        orderComments: getOrderComments(checkoutMetaInfo) || "",
        // we are setting paymentAmountWithoutDiscount only in case of invalid coupon alert flow
        // and we are setting updatedPaymentAmount in case of bad debit case
        paymentAmount: paymentAmountWithoutDiscount ?? updatedPaymentAmount ?? paymentAmount,
    } as PlaceOrderParams;

    if (isOriginalBusinessLineSwiggyMoney()) {
        let giftDetails: GiftDetails | undefined;

        try {
            // Setting this session storage key gift-details in swiggy money IW
            giftDetails = JSON.parse(sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY) as string);
        } catch {
            void 0;
        }

        placeOrderParamsData = { ...placeOrderParamsData, giftDetails };
    }

    if (isBusinessLineCBCC()) {
        const cbccfdApplicationNumber = GlobalHeaderState.getHeader(SwgyHeaderKeysList.swiggyApplicationNumber) || "";
        placeOrderParamsData = { ...placeOrderParamsData, cbccfdApplicationNumber };
    }

    const expenseCodeData = safeJsonParse(sessionStorage.getItem(SESSION_STORAGE_KEYS.EXPENSE_CODE_DATA) as string);

    // passing expenseCodeData to place order params
    if (!isEmpty(expenseCodeData)) {
        placeOrderParamsData = {
            ...placeOrderParamsData,
            expenseCodeData,
        };
    }

    return placeOrderParamsData;
};

export const getPartnerOrderIdFromOrderData = (data: OrderData): string => {
    if (data.paymentPartnerOrderId != null) {
        return String(data.paymentPartnerOrderId);
    }
    return String(data.paymentTxnId);
};

// transaction id is required for confirm order for some business line (edit order)
// as transaction id is there for every order and not specific to any flow, we are sending it always

export const getConfirmOrderParams = (orderData: OrderData): ConfirmOrderParams => {
    let confirmOrderParams: ConfirmOrderParams = {
        transactionId: orderData.paymentTxnId,
        orderId: orderData.orderId,
        paymentMethod: orderData?.orderPaymentMethod ?? "",
    };
    if (isOriginalBusinessLineSwiggyMoney()) {
        let giftDetails: GiftDetails | undefined;

        try {
            // Setting this session storage key gift-details in swiggy money IW
            giftDetails = JSON.parse(sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY) as string);
        } catch {
            void 0;
        }

        confirmOrderParams = { ...confirmOrderParams, giftDetails };
    }
    if (isBusinessLineDineout()) {
        confirmOrderParams = {
            ...confirmOrderParams,
            confirmOrderXp: orderData?.newDineoutConfirmOrderEnabledXp ?? false,
        };
    }

    return confirmOrderParams;
};

export const getOrderId = (data: OrderData): string => String(data.orderId);

export function hasSwiggyOnePurchaseItem(orderData: OrderData): boolean {
    return !isEmpty(orderData.orderSubscriptions);
}

export function isWebPollingEnabled(appRedirectionInfoString?: string | null): boolean {
    const appRedirectionInfo = safeJsonParse<AppRedirectionInfo>(appRedirectionInfoString as string, undefined);
    return !isEmpty(appRedirectionInfo?.pollingConfiguration?.pollingIntervalInMs);
}

export function handlePlaceOrderSuccess(
    response: ApiResponse<OrderApiData, RawOrderApiData>,
    type?: PLACE_ORDER_EVENT_TYPE,
    placeOrderData?: PlaceOrderData,
    isJuspaySdkAvailable?: boolean,
): PlaceOrderSuccessHandlerReturnType {
    const { statusCode, data, rawResponse, pendingPaymentParams } = response;

    if (statusCode !== API_SUCCESS_STATUS_CODE || data == null || data.orderData == null) {
        throw response;
    }
    const {
        orderPaymentMethod: legacyOrderPaymentMethod,
        orderId,
        confirmationTimeMinutes = "",
        credConfirmationTimeMinutes = "",
        targetedMarketplacePaymentReferenceId: targetedMarketplacePaymentForwardReferenceId,
        appRedirectionInfo: appRedirectionInfoString,
    } = data.orderData;

    const convertedAppRedirectionInfo = targetedMarketplacePaymentForwardReferenceId
        ? handleAppRedirectionInfoConversion(
              targetedMarketplacePaymentForwardReferenceId,
              appRedirectionInfoString,
              legacyOrderPaymentMethod,
          )
        : { orderPaymentMethod: legacyOrderPaymentMethod };

    const orderPaymentMethod = convertedAppRedirectionInfo?.orderPaymentMethod ?? legacyOrderPaymentMethod;

    let autoPayNRFields = {};

    const payonDeliveryVariant = placeOrderData?.extraParams?.pay_on_delivery?.variant;

    if (!isEmpty(payonDeliveryVariant)) {
        autoPayNRFields = {
            autoPayVariant: payonDeliveryVariant || "--",
            autoPayFailedOrderId: placeOrderData?.extraParams?.pay_on_delivery?.failed_order_id || "--",
        };
    }
    const isTurboPaymentMethod = getTurboPaymentMethodFromPlaceOrder(placeOrderData);
    recordNREvent(
        {
            orderId,
            eventType: type || NEWRELIC_TABLES.PAYMENTS_WEB_PLACE_ORDER,
            method: orderPaymentMethod,
            ...autoPayNRFields,
            isTurboPaymentMethod,
            confirmationTimeMinutes,
            credConfirmationTimeMinutes,
            webPollingEnabled: isWebPollingEnabled(appRedirectionInfoString).toString(),
            isSdkFlow: isJuspaySdkAvailable?.toString() || "--",
        },
        false,
    );
    // TODO: remove after IM loader debugging
    Storage.setItem(PLACE_ORDER_DATA, {
        orderId,
        orderPaymentMethod,
        isTurboPaymentMethod,
    });
    const placeOrderStatePayload = {
        ...data.orderData,
        ...convertedAppRedirectionInfo,
        xpData: data?.xpData,
        rawResponse: (rawResponse?.data as RawOrderApiData) ?? {},
        pendingPaymentParams: pendingPaymentParams ? (pendingPaymentParams as PendingPaymentParams) : null,
    };

    return { orderData: placeOrderStatePayload };
}

export function handlePlaceOrderFailure(err: unknown): void {
    captureError(err);
    showApiError(err);
}

export function handleConfirmOrderSuccess(response: ApiResponse<OrderApiData>): OrderData {
    const { statusCode, data } = response;
    if (statusCode !== API_SUCCESS_STATUS_CODE || data == null || data.orderData == null) {
        throw response;
    }

    return { ...data.orderData, xpData: data?.xpData };
}

export const getDirectPlaceOrderParams = (fromCart: boolean, cartTotal: number, orderComment: string) => {
    const placeOrderParams: PlaceOrderParams = {
        addressId: GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartAddressId) ?? "",
        // no payment method is required in case of direct place order
        paymentMethod: "",
    };

    if (isBusinessLineSwiggyOne()) {
        return {
            ...placeOrderParams,
            paymentMethod: CASH,
            lat: GlobalHeaderState.getHeader("lat") || "",
            lng: GlobalHeaderState.getHeader("lng") || "",
        };
    } else if (fromCart) {
        const extraParams = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            force_validate_coupon: true,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            transaction_amount: cartTotal,
        };

        return {
            ...placeOrderParams,
            ...extraParams,
            paymentMethod: CASH,
            lat: GlobalHeaderState.getHeader("lat") || "",
            lng: GlobalHeaderState.getHeader("lng") || "",
            useJuspayNative: false,
            paymentReturnUrl: "",
            orderComments: orderComment,
            paymentAmount: String(cartTotal),
        };
    }

    return placeOrderParams;
};

interface JuspayCardPaymentParams {
    method: CardPaymentMethodData;
    juspayMeta: OrderData["juspayMeta"];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraParams: any;
    cardBinNumber: string;
    merchantId?: string;
    tokenize?: boolean | null;
    nickname?: string;
    orderAmount: string;
    returnUrl: string;
    par?: string;
    clientAuthToken?: string;
}

export interface ProcessOrderReturnType {
    orderId?: string;
    paymentPartnerOrderId?: string;
    isOrderProcessed?: boolean;
    shouldRedirect?: boolean;
    redirectURI?: string;
    internalRedirect?: boolean;
    redirectFn?: VoidFunction;
    processSdkFlow?: boolean;
    amazonPayTransactionId?: string;
    status?: UPI_NB_STATUS;
}

// Throttle saved-card SDK invocations per order for dineout flows to prevent duplicate bridge calls.
const JUSPAY_DINEOUT_SAVED_CARD_THROTTLE_MS = 2000;
const JUSPAY_REGULAR_SAVED_CARD_THROTTLE_MS = 5000;
const processOrderSavedCardThrottleTracker = new Map<string, number>();
const logThrottleProcessOrderEvents = (type: string, details: Record<string, unknown> = {}) => {
    recordNREvent({
        eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
        type,
        ...details,
    });
};

const isEligibleForProcessOrderThrottle = (): boolean => {
    return (
        isBusinessLineRegular() ||
        isBusinessCategoryInstamart() ||
        isBusinessLineDineout() ||
        GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory)?.toLocaleLowerCase() ===
            MARKETPLACE_CATEGORY.DINING
    );
};

export const shouldThrottleProcessOrderForSavedCard = (processOrderPayload: ProcessOrderActionPayload): boolean => {
    try {
        if (!isEligibleForProcessOrderThrottle()) {
            logThrottleProcessOrderEvents("NO_PROCESS_ORDER_THROTTLE_NOT_DINEOUT");
            return false;
        }

        const JUSPAY_SAVED_CARD_THROTTLE_MS = isBusinessLineRegular()
            ? JUSPAY_REGULAR_SAVED_CARD_THROTTLE_MS
            : JUSPAY_DINEOUT_SAVED_CARD_THROTTLE_MS;

        const paymentPartnerOrderId = getPartnerOrderIdFromOrderData(processOrderPayload?.order);
        const paymentMethod = processOrderPayload?.placeOrderData?.paymentMethodData;
        const paymentEventDetails = {
            paymentPartnerOrderId,
            paymentMethod: paymentMethod?.paymentCode,
            turboPaymentMethod: processOrderPayload?.placeOrderData?.extraArbitaryData?.isTurboPaymentMethod,
            isCardPaymentMethod: String(isCardPaymentMethod(paymentMethod)),
            isAddNewCardPayment: String(isAddNewCardPayment(paymentMethod)),
        };

        if (
            !paymentEventDetails?.turboPaymentMethod ||
            !isCardPaymentMethod(paymentMethod) ||
            isAddNewCardPayment(paymentMethod)
        ) {
            logThrottleProcessOrderEvents("PROCESS_ORDER_THROTTLE_NOT_APPLICABLE", paymentEventDetails);
            return false;
        }

        const invocationTimestamp = Date.now();
        const lastInvocation = processOrderSavedCardThrottleTracker.get(paymentPartnerOrderId);
        const isThrottleApplicable =
            !isEmpty(lastInvocation) && invocationTimestamp - (lastInvocation ?? 0) <= JUSPAY_SAVED_CARD_THROTTLE_MS;

        if (isThrottleApplicable) {
            logThrottleProcessOrderEvents("THROTTLE_PLACE_ORDER_APPLICABLE", {
                ...paymentEventDetails,
                lastInvocationTimestamp: String(lastInvocation),
                invocationTimestamp: String(invocationTimestamp),
            });
            return true;
        }

        processOrderSavedCardThrottleTracker.set(paymentPartnerOrderId, invocationTimestamp);

        setTimeout(() => {
            const storedTimestamp = processOrderSavedCardThrottleTracker.get(paymentPartnerOrderId);
            if (storedTimestamp != null && storedTimestamp === invocationTimestamp) {
                processOrderSavedCardThrottleTracker.delete(paymentPartnerOrderId);
            }
        }, JUSPAY_SAVED_CARD_THROTTLE_MS);

        logThrottleProcessOrderEvents("THROTTLE_PROCESS_ORDER_NOT_APPLICABLE_FIRST_PROCESS_ORDER", {
            ...paymentEventDetails,
            invocationTimestamp: String(invocationTimestamp),
        });
        return false;
    } catch (error) {
        logThrottleProcessOrderEvents("PROCESS_ORDER_THROTTLE_ERROR", {
            errorMessage: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
};

export function payUsingJuspaySavedCard(
    orderId: string,
    params: JuspayCardPaymentParams,
): Promise<JuspayCardResponsePayload | undefined> {
    const cardCVV = !isEmpty(params.extraParams.cvv) ? params.extraParams.cvv : null;

    const juspayParams = {
        orderId,
        orderAmount: params.orderAmount,
        merchantId: params.merchantId || DEFAULT_JUSPAY_MERCHANTID,
        redirectAfterPayment: true,
        paymentMethodType: "CARD" as const,
        paymentMethod: params.method.meta.cardBrand ?? "",
        returnUrl: params.returnUrl,
        cardSecurityCode: cardCVV,
        cardToken: getPaymentCardToken(params.method),
        cardBin: params.cardBinNumber,
        nickname: params.nickname || "",
        tokenize: params.tokenize,
        saveToLocker: undefined,
        ...params.juspayMeta,
        ...(params.clientAuthToken && { juspayClientAuthToken: params.clientAuthToken }),
        ...(params.extraParams.isEmi === true && {
            isEmi: params.extraParams.isEmi,
            emiBank: String(params.extraParams.emiBank || ""),
            emiType: String(params.extraParams.emiType || ""),
            emiTenure: Number(params.extraParams.emiTenure || 0),
            supportedGatewayId: params.extraParams.supportedGatewayId,
        }),
    };

    if (!isEmpty(params.par)) {
        juspayParams["par"] = params.par || "";
    }

    if (!params.method?.meta?.cvvMandatory) {
        delete juspayParams["cardSecurityCode"];
    }

    return getBridgeSdk().payUsingJuspaySavedCard({
        callbackUrl: window.location.href,
        juspayParams,
    });
}

function payUsingJuspayNewCard(
    orderId: string,
    params: JuspayCardPaymentParams,
): Promise<JuspayCardResponsePayload | undefined> {
    return getBridgeSdk().payUsingJuspayNewCard({
        callbackUrl: window.location.href,
        juspayParams: {
            orderId,
            orderAmount: params.orderAmount,
            merchantId: params.merchantId || DEFAULT_JUSPAY_MERCHANTID,
            paymentMethodType: "CARD",
            returnUrl: params.returnUrl,
            cardSecurityCode: !isEmpty(params.extraParams.cvv) ? params.extraParams.cvv : null,
            saveToLocker: params.extraParams.saveCard,
            cardNumber: params.extraParams.cardNumber,
            cardExpMonth: params.extraParams.month,
            nameOnCard: params.extraParams.name,
            cardExpYear: params.extraParams.year,
            cardBin: params.cardBinNumber,
            nickname: params.nickname || "",
            tokenize: params.tokenize,
            par: params.par || "",
            ...params.juspayMeta,
            ...(params.clientAuthToken && { juspayClientAuthToken: params.clientAuthToken }),
            ...(params.extraParams.isEmi === true && {
                isEmi: params.extraParams.isEmi,
                emiBank: String(params.extraParams.emiBank || ""),
                emiType: String(params.extraParams.emiType || ""),
                emiTenure: Number(params.extraParams.emiTenure || 0),
                supportedGatewayId: params.extraParams.supportedGatewayId,
            }),
        },
    });
}

export const getCardBinNumber = (
    paymentMethodData: CardPaymentMethodData,
    extraParams: OnPayExtraParams | undefined = {},
): string => {
    return getCardBinFromExtraParams(extraParams) ?? paymentMethodData.bin ?? paymentMethodData.meta.cardIsin ?? "";
};

export const getJuspayClientAuthTokenForOrder = async (
    params: GetAuthTokenParams,
    isJuspayAuthTokenApiPublic?: boolean,
): Promise<JuspayClientAuthTokenData> => {
    const getJuspayClientAuthTokenFn = isJuspayAuthTokenApiPublic
        ? getPublicJuspayClientAuthToken
        : getJuspayClientAuthToken;

    const authTokenResponse = await getJuspayClientAuthTokenFn(params);
    return authTokenResponse;
};

export const getRedirectUrl = (order: OrderData): string => order?.paymentMethodMeta?.paymentRedirectUrl ?? "";

export const hasOrderProcessed = (order: OrderData): boolean => order.postStatus === POST_ORDER_STATUS.PROCESSING;

export async function processOrder(props: {
    processOrderData: ProcessOrderActionPayload;
    params: GetAuthTokenParams;
    extendedCustomerId: string;
    credOfferLayerDetails?: CredOfferLayerDetailsInterface;
    isJuspayAuthTokenApiPublic?: boolean;
    rawClientAuthTokenData?: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData[]>;
    paymentResponse?: GetPaymentMethodsResponse | undefined | null;
}): Promise<void | ProcessOrderReturnType> {
    const {
        processOrderData,
        params,
        extendedCustomerId,
        credOfferLayerDetails,
        isJuspayAuthTokenApiPublic,
        rawClientAuthTokenData,
        paymentResponse,
    } = props;
    const {
        order,
        placeOrderData: { paymentMethodData, extraParams },
    } = processOrderData;
    const sdkAvailable = processOrderData?.apayState?.available ?? false;
    const isNewAmazonPaySdkAvailable = processOrderData?.apayState?.isNewSdkAvailable ?? false;
    const isPhonepeSdkAvailable = processOrderData?.isPhonepeSdkAvailable ?? false;

    /** OrderId and PaymentPartnerOrderId are same for normal orders.
     * But we they are different in edit order (Eg: OrderId: 79879281967, paymentTxId: 79879281967E1)
     * We need to consider payment txn id here to make jus pay work as
     * juspay uses txn id in '/txns' api call to validate payments
     */
    // We need paymentPartnerOrderId whenever we are connecting with third party payment methods like Juspay etc.
    const paymentPartnerOrderId = getPartnerOrderIdFromOrderData(order);
    const orderId = String(order.paymentTxnId);
    const { name: methodName, meta } = paymentMethodData;
    const { upiIntentUrl } = order;

    const isEMIFlow = !isEmpty(extraParams?.isEmi);
    const emiMerchantId = resolveMerchantId(
        paymentResponse as GetPaymentMethodsResponse,
        VALID_PAYMENT_GROUP_TYPES.EMI,
    );

    const merchantId = isEMIFlow
        ? emiMerchantId
        : meta.merchantId || processOrderData.juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;

    const authToken = resolveClientAuthTokenDataForMerchantId(
        rawClientAuthTokenData,
        merchantId,
        DEFAULT_JUSPAY_MERCHANTID,
    )?.clientAuthToken;

    recordNREvent({
        eventType: NEWRELIC_TABLES.IMTR_DEBUG_LOGS,
        type: "ORDER_HELPER",
        params: JSON.stringify({
            meta: meta.merchantId,
            processOrderData: processOrderData.juspayMeta?.merchantId,
            isEmiFlow: isEMIFlow,
            emiMerchantId: emiMerchantId,
            token: authToken ?? "",
            method: paymentMethodData.groupName,
        }),
    });

    // only for independent flows of SM(gifting, add-money etc we want to modify the juspay url)
    const isIndependentSMflow = isBusinessLineSwiggyMoney() && isOriginalBusinessLineSwiggyMoney();
    if (hasOrderProcessed(order)) {
        return { orderId, paymentPartnerOrderId, isOrderProcessed: true };
    }

    getBridgeSdk()
        .setScreenCaptureState({ screenCapturingDisabled: true })
        .catch(() => void 0);

    try {
        if (isCardPaymentMethod(paymentMethodData)) {
            const isNewCard = isAddNewCardPayment(paymentMethodData);

            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                eventSubType: "card_order_helper",
                type: "CARD_ORDER_HELPER",
                isNewCard: String(isNewCard),
            });
            //Redirect flow
            const methodType = isNewCard ? VALID_PAYMENT_METHODS.NEW_CARD : VALID_PAYMENT_METHODS.CARD;

            if (!processOrderData.useJuspaySDK || shouldUseJuspayRedirectFlow()) {
                return payUsingJusPayRedirectFlow(processOrderData, methodType, merchantId);
            }

            const cardBinNumber = getCardBinNumber(paymentMethodData, extraParams);

            if (cardBinNumber !== extraParams?.cardBin && isNewCard) {
                // TODO: Remove this after debugging the irregular card bin issue
                recordNREvent({
                    eventType: NEWRELIC_TABLES.INCORRECT_CARD_BIN,
                });
            }

            //SDK flow
            const params: JuspayCardPaymentParams = {
                extraParams,
                cardBinNumber,
                // primaryPaymentTransactionAmount is the correct amount needs to be passed to juspay, as orderTotal is not correct amount in case of splitpay payments
                orderAmount: String(order?.primaryPaymentTransactionAmount ?? order.orderTotal),
                method: paymentMethodData,
                juspayMeta: order.juspayMeta,
                tokenize: getCardTokenizeValue(paymentMethodData.meta, extraParams),
                merchantId: merchantId,
                returnUrl: isIndependentSMflow ? SM_JUSPAY_END_URLS_REGEX : JUSPAY_END_URLS_REGEX,
                par: (paymentMethodData?.meta?.token as JuspayTokenMetaInterface)?.par ?? "",
                clientAuthToken: authToken,
            };

            if (extraParams && extraParams.nickName) {
                params.nickname = extraParams.nickName;
            }

            //While paying with juspay a flag webviewRefreshed as true is sent in saveUsersLastState native bridge.
            //When webviewRefreshed is true we would know it is a webview refresh flow and orderId, paymentPartnerOrderId would be
            //returned and confirm order call would be made
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "orderHelperCard",
                paymentTxnId: orderId,
                orderId: order.orderId || "",
                paymentPartnerOrderId: paymentPartnerOrderId,
                webviewRefreshed: String(processOrderData?.webviewRefreshExtraParams?.webviewRefreshed),
            });
            if (processOrderData?.webviewRefreshExtraParams?.webviewRefreshed) {
                return { orderId, paymentPartnerOrderId };
            } else {
                await saveUsersLastStateDuringProcessOrder(processOrderData, PAYMENT_CODES.JUSPAY);
            }
            await (isNewCard
                ? payUsingJuspayNewCard(paymentPartnerOrderId, params)
                : payUsingJuspaySavedCard(paymentPartnerOrderId, params));
            await clearWebviewRefreshSavedState();
            return { orderId, paymentPartnerOrderId };
        } else if (isNetBankingPaymentMethod(paymentMethodData)) {
            //Redirect Flow
            if (!processOrderData.useJuspaySDK || shouldUseJuspayRedirectFlow()) {
                return payUsingJusPayRedirectFlow(processOrderData, VALID_PAYMENT_METHODS.NETBANKING, merchantId);
            }

            //While paying with juspay netbanking a flag webviewRefreshed as true is sent in saveUsersLastState native bridge.
            //When webviewRefreshed is true we would know it is a webview refresh flow and orderId, paymentPartnerOrderId would be
            //returned and confirm order call would be made
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "orderHelperNetbanking",
                paymentTxnId: orderId,
                orderId: order.orderId || "",
                paymentPartnerOrderId: paymentPartnerOrderId,
                webviewRefreshed: String(processOrderData?.webviewRefreshExtraParams?.webviewRefreshed),
            });
            if (processOrderData?.webviewRefreshExtraParams?.webviewRefreshed) {
                return { orderId, paymentPartnerOrderId };
            } else {
                await saveUsersLastStateDuringProcessOrder(processOrderData, PAYMENT_CODES.NETBANKING);
            }

            //SDK Flow
            await getBridgeSdk().payUsingJuspayNB({
                callbackUrl: window.location.href,
                juspayParams: {
                    orderId: paymentPartnerOrderId,
                    merchantId: merchantId,
                    paymentMethodType: "NB",
                    bankCode: paymentMethodData.nbCode,
                    returnUrl: isIndependentSMflow ? SM_JUSPAY_END_URLS_REGEX : JUSPAY_END_URLS_REGEX,
                    ...order.juspayMeta,
                    ...(authToken && { juspayClientAuthToken: authToken }),
                },
            });
            await clearWebviewRefreshSavedState();
        } else if (isWalletPaymentMethod(paymentMethodData)) {
            let redirectUrl: string | undefined;

            // If it phone redirect/sdk flow
            if (isPaymentMethodPhonePe(paymentMethodData)) {
                //sdk flow
                if (isPhonepeSdkAvailable) {
                    const phoneRequisites = order.phonepeDebitRequisites;
                    await getBridgeSdk().payUsingPhonepeSdk({
                        orderId: paymentPartnerOrderId,
                        phonePeData: {
                            base64Body: phoneRequisites?.base64Body,
                            checksum: phoneRequisites?.checksum,
                            apiEndPoint: phoneRequisites?.apiEndPoint,
                            s2sCallBackUrl: phoneRequisites?.s2SCallBackUrl,
                            transactionId: phoneRequisites?.transactionId,
                            isPhonePeUpiFlow: false,
                        },
                        ...(params.tenant && { tenant: params.tenant as Tenant }),
                    });

                    return {
                        orderId,
                        paymentPartnerOrderId,
                    };
                } else {
                    //redirect flow
                    redirectUrl = generatePhonepeRedirectUrl(order);
                }
            } else if (isPaymentMethodAmazonPay(paymentMethodData)) {
                if (sdkAvailable && isPaymentMethodAmazonPaySDK(paymentMethodData)) {
                    //While paying with amazonPay a flag webviewRefreshed as true is sent in saveUsersLastState native bridge.
                    //When webviewRefreshed is true we would know it is a webview refresh flow.
                    //App would add amazonPayTransactionId in the saved state so that api/v1/amazonpay/sign-and-encrypt api call can be made
                    // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "orderHelperAmazonPay",
                        paymentTxnId: orderId,
                        orderId: order.orderId || "",
                        paymentPartnerOrderId: paymentPartnerOrderId,
                        amazonPayTransactionId:
                            processOrderData.webviewRefreshExtraParams?.amazonPayTransactionId || "",
                        webviewRefreshed: String(processOrderData?.webviewRefreshExtraParams?.webviewRefreshed),
                    });
                    if (processOrderData.webviewRefreshExtraParams?.webviewRefreshed) {
                        if (!processOrderData.webviewRefreshExtraParams.amazonPayTransactionId) {
                            return { orderId, paymentPartnerOrderId };
                        }

                        return {
                            orderId,
                            paymentPartnerOrderId,
                            processSdkFlow: true,
                            amazonPayTransactionId: processOrderData.webviewRefreshExtraParams.amazonPayTransactionId,
                        };
                    } else if (isNewAmazonPaySdkAvailable) {
                        const payUrl = getRedirectUrl(order);

                        if (!isEmpty(payUrl)) {
                            await saveUsersLastStateDuringProcessOrder(processOrderData, PAYMENT_CODES.AMAZONPAY);

                            const nbStart = Date.now();
                            await getBridgeSdk().payUsingAmazonPay({
                                state: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY_V2,
                                data: {
                                    requestId: paymentPartnerOrderId,
                                    payUrl,
                                },
                                ...(params.tenant && { tenant: params.tenant as Tenant }),
                            });
                            recordNativeBridgeCall({
                                nbState: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY_V2,
                                isNewSdk: true,
                                status: APAY_NB_CALL_STATUS.SUCCESS,
                                latencyMs: Date.now() - nbStart,
                            });

                            await clearWebviewRefreshSavedState();
                        } else {
                            recordNewSdkFlowEvent(AMAZON_PAY_CUSTOM_EVENTS.PROCEED_TO_PAY_V2_EMPTY_PAY_URL, {
                                orderId,
                                paymentPartnerOrderId,
                            });
                        }

                        // start polling flow
                        return {
                            orderId,
                            paymentPartnerOrderId,
                        };
                    } else {
                        await saveUsersLastStateDuringProcessOrder(processOrderData, PAYMENT_CODES.AMAZONPAY);

                        const nbStart = Date.now();
                        const response = await getBridgeSdk().payUsingAmazonPay({
                            state: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY,
                            data: {
                                callbackUrl: window.location.href,
                                walletParams: {
                                    orderId: paymentPartnerOrderId,
                                    orderAmount: "",
                                    ...order.paymentMethodMeta,
                                },
                            },
                            ...(params.tenant && { tenant: params.tenant as Tenant }),
                        });
                        recordNativeBridgeCall({
                            nbState: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY,
                            isNewSdk: false,
                            status: APAY_NB_CALL_STATUS.SUCCESS,
                            latencyMs: Date.now() - nbStart,
                        });
                        await clearWebviewRefreshSavedState();

                        return {
                            orderId,
                            paymentPartnerOrderId,
                            processSdkFlow: true,
                            amazonPayTransactionId: (response as AmazonSdkProceedToPayResponseData)?.transactionId,
                        };
                    }
                } else {
                    //redirect flow
                    redirectUrl = getRedirectUrl(order);
                }
            }
            // In case of other wallet methods return orderId (i.e. no redirection)
            else {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "wallet_flow",
                    type: "WALLET_FLOW",
                });
                return { orderId, paymentPartnerOrderId };
            }

            // throw Error in case if redirect URL in not valid
            if (typeof redirectUrl !== "string" || !isValidURI(redirectUrl)) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "wallet_flow_error",
                    type: "WALLET_FLOW_ERROR",
                });
                throw new Error(OOPS_SOMETHING_WENT_WRONG);
            }

            return {
                orderId,
                paymentPartnerOrderId,
                shouldRedirect: true,
                redirectURI: redirectUrl,
            };
        } else if (isPaymentMethodUpiAndPaymentCodePhonePeUpi(paymentMethodData)) {
            const phoneRequisites = order.phonepeDebitRequisites;
            await getBridgeSdk().payUsingPhonepeSdk({
                orderId: paymentPartnerOrderId,
                phonePeData: {
                    base64Body: phoneRequisites?.base64Body,
                    checksum: phoneRequisites?.checksum,
                    apiEndPoint: phoneRequisites?.apiEndPoint,
                    s2sCallBackUrl: phoneRequisites?.s2SCallBackUrl,
                    transactionId: phoneRequisites?.transactionId,
                    isPhonePeUpiFlow: true,
                    packageName: paymentMethodData.name,
                },
                ...(params.tenant && { tenant: params.tenant as Tenant }),
            });
        } else if (isPaymentMethodUpiAndPaymentCodeUpiIntent(paymentMethodData)) {
            // throw Error in case if upiIntent URL in not
            if (typeof upiIntentUrl !== "string" || !upiIntentUrl) {
                throw new Error(OOPS_SOMETHING_WENT_WRONG);
            }
            const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
            const versionCode = getVersionCode();
            if (
                isAndroidPlatform(platform) &&
                !isBusinessLineSwiggyMoney() &&
                !notAllowedWebviewRefreshVersionCodes.includes(versionCode)
            ) {
                // Save UPI intent state whenever user opts for UPI intent flow
                try {
                    // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "orderHelperUpi",
                        paymentTxnId: orderId,
                        orderId: order.orderId || "",
                        paymentPartnerOrderId,
                    });
                } catch {
                    /* No-op */
                }
                const payload = {
                    state: CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER,
                    infoObject: {
                        order,
                        placeOrderData: processOrderData.placeOrderData,
                        startTime: Date.now(),
                    },
                    paymentCode: PAYMENT_CODES.UPIINTENT,
                };
                try {
                    setPersistOrderDataInLocalStorage(payload, SetDataSource.UPI_POLLER);
                } catch (_) {
                    // no-op
                }

                await getBridgeSdk()
                    .saveUsersLastState(payload)
                    .catch(() => void 0);
            }

            // UPI intent: mweb/dweb redirect in-package; native / Ixigo bridge use NB
            if (shouldUseGpoUpiIntentAppsOnWebCheckout()) {
                try {
                    await handleMwebUpiIntentRedirect(upiIntentUrl, paymentPartnerOrderId);
                } catch {
                    return {
                        orderId,
                        paymentPartnerOrderId,
                        status: UPI_NB_STATUS.MWEB_UPI_APP_REDIRECT_FAILURE,
                    };
                }
            } else {
                await getBridgeSdk().payUsingUpiIntent({
                    callbackUrl: window.location.href,
                    upiParams: {
                        orderId: paymentPartnerOrderId,
                        methodName,
                        orderAmount: "",
                        url: upiIntentUrl,
                    },
                });
            }
        } else if (isCredPaymentMethod(paymentMethodData)) {
            await getBridgeSdk().payUsingJuspayWalletCred({
                callbackUrl: window.location.href,
                juspayParams: {
                    orderId: paymentPartnerOrderId,
                    paymentMethodType: paymentMethodData.name,
                    returnUrl: JUSPAY_SWIGGY_RETURN_URL,
                    clientAuthToken: authToken || (order.juspayMeta.juspayClientAuthToken ?? ""),
                    merchantId,
                },
            });
        } else if (isInAppUpiPaymentMethod(paymentMethodData)) {
            const tokenData = await getJuspayClientAuthTokenForOrder(params, isJuspayAuthTokenApiPublic);
            await handlePayUsingInAppUpi(paymentMethodData, processOrderData, paymentPartnerOrderId, {
                clientAuthToken: tokenData.clientAuthToken,
                tenant: params.tenant,
                extendedCustomerId,
                credOfferLayerDetails: credOfferLayerDetails || {},
                merchantId: tokenData?.mid || merchantId,
            });
        } else if (isCreditCardEMIPaymentMethod(paymentMethodData)) {
            // REDIRECT FLOW , take user to EMI page returned from juspay
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                eventSubType: "emi_payment_group_error",
                type: "EMI_PAYMENT_GROUP_ERROR",
            });
            const emiWebviewUrl = order.emiRedirectUrl;
            // throw Error in case if redirect URL in not valid
            if (!isValidURI(emiWebviewUrl)) {
                recordNREvent(
                    {
                        orderId,
                        eventType: NEWRELIC_TABLES.EMI_PAYMENT_GROUP_ERROR,
                        emiPaymentUrl: emiWebviewUrl || "",
                    },
                    false,
                );
                throw new Error(OOPS_SOMETHING_WENT_WRONG);
            }

            return {
                orderId,
                paymentPartnerOrderId,
                shouldRedirect: true,
                redirectURI: emiWebviewUrl,
            };
        } else if (isPaymentMethodAmazonPayLater(paymentMethodData)) {
            if (processOrderData.webviewRefreshExtraParams?.webviewRefreshed) {
                return { orderId, paymentPartnerOrderId };
            }

            const payUrl = getRedirectUrl(order);

            if (!isEmpty(payUrl)) {
                await saveUsersLastStateDuringProcessOrder(processOrderData, PAYMENT_CODES.AMAZONPAYLATER);

                const nbStart = Date.now();
                await getBridgeSdk().payUsingAmazonPay({
                    state: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY_V2,
                    data: {
                        requestId: paymentPartnerOrderId,
                        payUrl,
                    },
                    ...(params.tenant && { tenant: params.tenant as Tenant }),
                });
                recordNativeBridgeCall({
                    nbState: AMAZON_PAY_NB_STATES.PROCEED_TO_PAY_V2,
                    isNewSdk: true,
                    status: APAY_NB_CALL_STATUS.SUCCESS,
                    latencyMs: Date.now() - nbStart,
                    extra: { method: PAYMENT_CODES.AMAZONPAYLATER },
                });

                await clearWebviewRefreshSavedState();
            } else {
                recordNewSdkFlowEvent(AMAZON_PAY_CUSTOM_EVENTS.PROCEED_TO_PAY_V2_EMPTY_PAY_URL, {
                    orderId,
                    paymentPartnerOrderId,
                    method: PAYMENT_CODES.AMAZONPAYLATER,
                });
            }

            return { orderId, paymentPartnerOrderId };
        }

        return { orderId, paymentPartnerOrderId };
    } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { orderId, paymentPartnerOrderId, status: (e as any)?.status || UPI_NB_STATUS.FAILURE };
    }
}

export function isSuperUserOrder(orderData: OrderData): boolean {
    const { orderTags, successMessageType } = orderData;
    if (successMessageType && successMessageType.toUpperCase() === SUPER_ORDER_TAG) {
        return true;
    }

    return (orderTags && orderTags.includes(SUPER_ORDER_TAG)) ?? false;
}

export function isPriorityDeliveryOrder(orderData: OrderData): boolean {
    const { selectedSlaOption, priorityDeliveryFee = 0 } = orderData;

    return !!(
        selectedSlaOption &&
        selectedSlaOption.toUpperCase() === PRIORITY_DELIVERY_TAG &&
        priorityDeliveryFee > 0
    );
}

export function getOrderSuccessMessage(response: ApiResponse<OrderApiData>): {
    successMessageType: string;
    successMessage?: string;
    successMessageInfo?: string;
    successMessageV2?: string;
    successMessageInfoV2?: string;
} | null {
    const { data, statusCode } = response;
    if (statusCode !== API_SUCCESS_STATUS_CODE || data === null || data.orderData === null) {
        return null;
    }

    const { successMessageType, successMessage, successMessageInfo, successMessageInfoV2, successMessageV2 } =
        data.orderData;

    return {
        successMessageType,
        successMessage,
        successMessageInfo,
        successMessageV2,
        successMessageInfoV2,
    };
}

export function canCapturePendingPaymentState(placeOrderData: PlaceOrderData): boolean {
    return (
        ALLOWED_METHODS_FOR_PENDING_PAYMENT.map((paymentCode) => paymentCode.toUpperCase()).indexOf(
            placeOrderData.paymentMethodData.paymentCode.toUpperCase(),
        ) > -1
    );
}

export function allowedMethodAppStateInIdleMode(paymentCode: string): boolean {
    return isIos() && ALLOWED_METHODS_APP_STATE_IN_IDLE_MODE.indexOf(paymentCode.toUpperCase()) > -1;
}

export function getJuspayBackURL(
    paymentReturnUrl: string | undefined,
    addSwiggyMoneyReturnUrl: string | undefined,
): string {
    const backURL = isBusinessLineSwiggyMoney()
        ? getSwiggyMoneyReceivedRedirectUrl(addSwiggyMoneyReturnUrl)
        : getOrderConfirmationRedirectUrl(paymentReturnUrl);
    return backURL;
}
